// The words and the small rules behind Nateva's notifications (the bell and the Notifications page).
// Pure functions, no React, so they are tested on their own (notifications.test.mjs).
// The notices themselves come from nateva-platform (migrations 0085 to 0089, public.notify_*).

export interface Notice {
  id: string
  kind: string
  kind_label: string
  app: string
  title: string
  body: string | null
  link: string | null
  created_at: string
  read_at: string | null
  is_sample: boolean
}

export interface AdminNotice extends Notice {
  person_email: string
  person_name: string | null
}

export interface EmailCopy {
  id: string
  notice_id: string
  to_email: string
  person_name: string | null
  subject: string
  body: string
  status: string
  note: string
  created_at: string
  is_sample: boolean
}

export interface KindInfo {
  slug: string
  app: string
  label: string
  plain: string
  mutable: boolean
}

export interface MySettings {
  email_copy: boolean
  muted: string[]
  sending: boolean
  kinds: KindInfo[]
}

export interface KindCount {
  kind: string
  label: string
  app: string
  total: number
  last_7_days: number
  unread: number
}

export interface Summary {
  sending: boolean
  notices: number
  unread: number
  last_7_days: number
  held_copies: number
  people_with_copies: number
  people_with_mutes: number
  by_kind: KindCount[]
  last_sweep: { ran_at: string; ran_by: string | null; made: Record<string, number> } | null
}

/** The name of each app or area a notice can come from, in Nateva's words. */
export const APP_LABEL: Record<string, string> = {
  field: 'Field',
  partners: 'Partners',
  agreements: 'Agreements',
  yield: 'Yield',
  carbon: 'Carbon',
  estate: 'Estate',
  reports: 'Reports',
  programme: 'Programme',
  access: 'Access',
}

export const APP_ORDER = ['agreements', 'partners', 'field', 'yield', 'carbon', 'estate', 'reports', 'programme', 'access'] as const

export function appLabel(app: string): string {
  return APP_LABEL[app] ?? app.charAt(0).toUpperCase() + app.slice(1)
}

/** "Just now", "5 min ago", "3 h ago", "Yesterday", "4 days ago", then the date. */
export function whenWords(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return ''
  const mins = Math.round((now - t) / 60_000)
  if (mins < 2) return 'Just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} h ago`
  const days = Math.round(hrs / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return new Date(t).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: new Date(t).getFullYear() === new Date(now).getFullYear() ? undefined : 'numeric' })
}

/** A full date and time for the admin lists, in New Zealand time. */
export function stampWords(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Pacific/Auckland' })
}

export type Target = { kind: 'path'; path: string } | { kind: 'url'; url: string } | { kind: 'none' }

/**
 * Where a notice opens. A path on the front door (starts with one slash) opens in place; a full https address
 * (an app) opens in that app; anything else (empty, a script, a protocol-less string) opens nothing.
 */
export function linkTarget(link: string | null | undefined): Target {
  const l = (link ?? '').trim()
  if (!l) return { kind: 'none' }
  if (l.startsWith('/') && !l.startsWith('//')) return { kind: 'path', path: l }
  if (/^https:\/\/[a-z0-9.-]+(?::\d+)?(\/|$)/i.test(l)) return { kind: 'url', url: l }
  return { kind: 'none' }
}

/** Newest first, then the app, for a list the person has filtered. */
export function filterNotices<T extends Notice>(list: T[], f: { app: string | null; kind: string | null; unreadOnly: boolean }): T[] {
  return list.filter((n) => (!f.app || n.app === f.app) && (!f.kind || n.kind === f.kind) && (!f.unreadOnly || !n.read_at))
}

/** How many notices each app has (and how many are unread), in the order people expect. */
export function countByApp(list: Notice[]): { app: string; total: number; unread: number }[] {
  const m = new Map<string, { total: number; unread: number }>()
  for (const n of list) {
    const c = m.get(n.app) ?? { total: 0, unread: 0 }
    c.total++
    if (!n.read_at) c.unread++
    m.set(n.app, c)
  }
  const known = APP_ORDER.filter((a) => m.has(a))
  const rest = [...m.keys()].filter((a) => !(APP_ORDER as readonly string[]).includes(a)).sort()
  return [...known, ...rest].map((app) => ({ app, ...m.get(app)! }))
}

/** The kinds of one app, for the settings page, in the order the database gives them. */
export function kindsByApp(kinds: KindInfo[]): { app: string; kinds: KindInfo[] }[] {
  const apps = [...new Set(kinds.map((k) => k.app))]
  apps.sort((a, b) => {
    const ia = (APP_ORDER as readonly string[]).indexOf(a)
    const ib = (APP_ORDER as readonly string[]).indexOf(b)
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
  })
  return apps.map((app) => ({ app, kinds: kinds.filter((k) => k.app === app) }))
}

/** A muted list with one kind switched on or off. Kinds that cannot be muted are never added. */
export function toggleMuted(muted: string[], kind: KindInfo, mute: boolean): string[] {
  if (!kind.mutable) return muted
  const rest = muted.filter((m) => m !== kind.slug)
  return mute ? [...rest, kind.slug].sort() : rest
}

/** "3 unread", "1 unread", "All read". */
export function unreadLabel(n: number): string {
  return n > 0 ? `${n} unread` : 'All read'
}
